import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { SEComponent, SEContainerComponent } from '@delon/abc/se';
import { FormsModule } from '@angular/forms';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { DateRangeComponent } from '../../../../../routes/reporting/reports/_shared/components/range/date-range/date-range.component';
import { Router } from '@angular/router';
import { CopyTransactionService } from '@shared-feature/components/actions/doc-edit-actions/copy-transaction/copy-transaction.service';
import {
  ACL_TRANSACTION_TYPE_MAP,
  TRANSACTION_TYPE_TO_URL_MAP,
  TransactionType,
  URL_TO_TRANSACTION_TYPE_MAP
} from '@shared-feature/components/actions/doc-edit-actions/transaction-type-mapper';
import { CurrencyPricePipe } from '@delon/util';
import { NgForOf, NgIf } from '@angular/common';
import {
  NzCellAlignDirective,
  NzTableCellDirective,
  NzTableComponent,
  NzTbodyComponent,
  NzTheadComponent,
  NzThMeasureDirective,
  NzTrDirective
} from 'ng-zorro-antd/table';
import {
  NzResizableDirective,
  NzResizeHandleComponent
} from 'ng-zorro-antd/resizable';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import { SharedModule } from '@shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingsService } from '@delon/theme';
import {
  LicenseUtilsService,
  Plan,
  Region
} from '../../../../../shared/services/license-utils.service';
import { ACLService } from '@delon/acl';
import { DocBaseService } from '@shared-feature/services/_base/doc-base.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-copy-transaction-modal',
  standalone: true,
  imports: [
    NzModalModule,
    NzSelectComponent,
    SEComponent,
    FormsModule,
    NzFormDirective,
    SEContainerComponent,
    DateRangeComponent,
    CurrencyPricePipe,
    NgForOf,
    NgIf,
    NzCellAlignDirective,
    NzResizableDirective,
    NzResizeHandleComponent,
    NzSwitchComponent,
    NzTableCellDirective,
    NzTableComponent,
    NzTbodyComponent,
    NzThMeasureDirective,
    NzTheadComponent,
    NzTrDirective,
    SharedModule
  ],
  templateUrl: './copy-transaction.component.html',
  styleUrl: './copy-transaction.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CopyTransactionComponent implements OnInit {
  #licenseService = inject(LicenseUtilsService);
  #settingsService = inject(SettingsService);
  srv = inject(DocBaseService);
  #copyTransactionService = inject(CopyTransactionService);
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);
  #aclService = inject(ACLService);

  requestApplyCopy = output<any>();
  closeModal = output<void>();
  data: any = {};

  copyType = input.required<'TO' | 'FROM'>();
  id = input.required<any>();
  transactions = signal<Transaction[] | any[]>([]);
  selectedTransactionId = signal<string | number | null>(null);
  license = signal<any>(null);
  loading = signal(false);

  /**
   * Get the current TransactionType based on the current URL
   */
  get currentTransactionType(): TransactionType | null {
    const url = this.#router.url;
    const segments = url.split('/').filter(segment => segment.length > 0);

    if (segments.length >= 2) {
      const contextTypeKey = `${segments[0].toLowerCase()}/${segments[1].toLowerCase()}`;
      return URL_TO_TRANSACTION_TYPE_MAP[contextTypeKey] || null;
    }

    return null;
  }

  /**
   * Helper method to get ACL key for a transaction type
   * Maps TransactionType to the actual permission keys used in the system
   */
  #getAclKeyForTransactionType(transactionType: TransactionType): string {
    return ACL_TRANSACTION_TYPE_MAP[transactionType] || '';
  }

  /**
   * Check if user has specific permission for a transaction type
   */
  #hasPermission(
    transactionType: TransactionType,
    permission: 'view' | 'create' | 'update'
  ): boolean {
    const aclKey = this.#getAclKeyForTransactionType(transactionType);
    if (!aclKey) return false;

    const permissionKey = `${aclKey}.${permission}`;

    return this.#aclService.can(permissionKey);
  }

  /**
   * Determine if a transaction type should be available for Copy TO
   * Logic: View=1 AND Create=1
   */
  #isAvailableForCopyTo(transactionType: TransactionType): boolean {
    const hasView = this.#hasPermission(transactionType, 'view');
    const hasCreate = this.#hasPermission(transactionType, 'create');

    return hasView && hasCreate;
  }

  /**
   * Determine if a transaction type should be available for Copy FROM
   * Logic: (View=1 AND Create=1) OR (View=1 AND Modify=1)
   */
  #isAvailableForCopyFrom(transactionType: TransactionType): boolean {
    const hasView = this.#hasPermission(transactionType, 'view');
    const hasCreate = this.#hasPermission(transactionType, 'create');
    const hasModify = this.#hasPermission(transactionType, 'update');

    return hasView && (hasCreate || hasModify);
  }

  /**
   * Computed property that filters transaction types based on ACL permissions and copy type
   */
  filteredTransactionTypeOptions = computed(() => {
    const baseOptions = this.transactionTypeOptions();
    const mode = this.copyType();

    return baseOptions.filter(opt => {
      const transactionType = opt.value as TransactionType;

      if (mode === 'TO') {
        return this.#isAvailableForCopyTo(transactionType);
      } else {
        return this.#isAvailableForCopyFrom(transactionType);
      }
    });
  });

  /**
   * Computed property that filters transaction types based on plan restrictions
   */
  transactionTypeOptions = computed(() => {
    const licenseData = this.#licenseService.license();

    if (!licenseData) {
      return Object.values(TransactionType).map(type => ({
        label: type,
        value: type
      }));
    }

    const plan: any = this.#licenseService.planName();
    const region = this.#settingsService.user?.company?.country as Region;

    // Safety check for region
    if (!region || !['PH', 'MY', 'MM'].includes(region)) {
      return Object.values(TransactionType).map(type => ({
        label: type,
        value: type
      }));
    }

    const allTypes = Object.values(TransactionType);

    const restrictedTypes = PLAN_RESTRICTIONS[region]?.[plan] || [];

    let availableTypes = allTypes.filter(
      type => !restrictedTypes.includes(type)
    );

    if (plan === 'PRO') {
      const hasMultiLocationAddon =
        this.#licenseService.multiLocationsEnabled();

      if (!hasMultiLocationAddon) {
        availableTypes = availableTypes.filter(
          type => type !== TransactionType.StockTransfer
        );
      }
    }

    // Business Rule: Prevent copying TO refund types
    // When in Copy TO mode, always exclude refund types from the dropdown
    if (this.copyType() === 'TO') {
      availableTypes = availableTypes.filter(
        type =>
          type !== TransactionType.CashSaleRefund &&
          type !== TransactionType.CashPurchaseRefund
      );
    }

    return availableTypes.map(type => ({
      label: type,
      value: type
    }));
  });

  /**
   * Check if a transaction type is available
   */
  isTransactionTypeAvailable(type: TransactionType): boolean {
    return this.filteredTransactionTypeOptions().some(
      opt => opt.value === type
    );
  }

  #getUrlSegmentForTransactionType(
    transactionType: TransactionType
  ): string | null {
    return TRANSACTION_TYPE_TO_URL_MAP[transactionType] || null;
  }

  #getTransactionTypeKey(source: boolean) {
    return !source
      ? this.#getUrlSegmentForTransactionType(this.data.type)
      : this.#getUrlSegmentForTransactionType(
          this.currentTransactionType as any
        )?.split('/')[1];
  }

  ngOnInit(): void {
    this.#licenseService.getLicense();

    if (this.copyType() === 'FROM' && this.data.type) {
      if (!this.isTransactionTypeAvailable(this.data.type)) {
        this.data.type = null;
        this.transactions.set([]);
      }
    }

    if (this.copyType() === 'FROM') {
      this.data.start = new Date();
      this.data.end = new Date();

      if (this.data.type) {
        this.#onSearchTransactions(this.data.type);
      }
    }
  }

  #onSearchTransactions(type: any) {
    this.selectedTransactionId.set(null);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const data = {
      docType: type ?? this.data.type,
      docDateFrom: formatDate(this.data.start),
      docDateTo: formatDate(this.data.end)
    };

    this.loading.set(true);
    this.#copyTransactionService
      .searchTransactions(data)
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: res => {
          this.transactions.set(res);
        }
      });
  }

  #getTransactionTypeRouteBase() {
    return this.copyType() === 'TO'
      ? this.#getUrlSegmentForTransactionType(this.data.type)
      : this.#getUrlSegmentForTransactionType(
          this.currentTransactionType as any
        );
  }

  onOk() {
    let emitData: any;

    if (this.copyType() === 'TO') {
      if (!this.data.type) {
        return;
      }

      if (!this.isTransactionTypeAvailable(this.data.type)) {
        return;
      }

      emitData = {
        routeBase: this.#getTransactionTypeRouteBase(),
        sourceKey: this.#getTransactionTypeKey(true),
        destKey: this.#getTransactionTypeKey(false),
        source: this.currentTransactionType,
        destination: this.data.type,
        id: this.id(),
        type: this.copyType()
      };
    } else {
      if (!this.selectedTransactionId()) {
        return;
      }

      if (!this.data.type) {
        return;
      }

      if (!this.isTransactionTypeAvailable(this.data.type)) {
        return;
      }

      emitData = {
        routeBase: this.#getTransactionTypeRouteBase(),
        sourceKey: this.#getTransactionTypeKey(false),
        destKey: this.#getTransactionTypeKey(true),
        source: this.data.type,
        destination: this.currentTransactionType,
        id: this.selectedTransactionId(),
        type: this.copyType()
      };
    }

    this.requestApplyCopy.emit(emitData);
    this.#copyTransactionService.copyIsActivated.set(true);
  }

  onTypeChange($event: any) {
    this.transactions.set([]);
    this.selectedTransactionId.set(null);
    if ($event) {
      this.#onSearchTransactions($event);
    }
  }

  onSearch() {
    this.transactions.set([]);
    this.selectedTransactionId.set(null);
    this.#onSearchTransactions(this.data.type);
  }

  onCancel() {
    this.closeModal.emit();
  }

  onToggleCopy(item: any) {
    const currentSelected = this.selectedTransactionId();
    if (currentSelected === item.id) {
      this.selectedTransactionId.set(null);
    } else {
      this.selectedTransactionId.set(item.id);
    }
  }

  isTransactionSelected(item: any): boolean {
    return this.selectedTransactionId() === item.id;
  }

  columns: any[] = [
    {
      title: 'Date',
      width: '120px',
      align: 'center'
    },
    {
      title: 'Doc #',
      width: '120px',
      align: 'center'
    },
    {
      title: 'Customer Name',
      index: 'customerName',
      width: '200px'
    },
    {
      title: 'Net Amount',
      align: 'right',
      width: '120px'
    }
  ];
}

type Transaction = {
  id: string | number;
  docDate: string | Date;
  docCode: string;
  customerName: string;
  netTotalAmountLocal: any;
  isTransfer: any;
};

const PLAN_RESTRICTIONS: Record<
  Region,
  Partial<Record<Plan, TransactionType[]>>
> = {
  PH: {
    LITE: [
      TransactionType.Quotation,
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.SalesInvoice,
      TransactionType.SalesDebitNote,
      TransactionType.SalesCreditNote,
      TransactionType.CashSales,
      TransactionType.CashSaleRefund,
      TransactionType.PurchaseRequisition,
      TransactionType.PurchaseOrder,
      TransactionType.GoodsReceivedNote,
      TransactionType.PurchaseInvoice,
      TransactionType.PurchaseDebitNote,
      TransactionType.PurchaseReturn,
      TransactionType.CashPurchases,
      TransactionType.CashPurchaseRefund,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer,
      TransactionType.StockIn,
      TransactionType.StockOut
    ],
    BASIC: [
      TransactionType.Quotation,
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.SalesInvoice,
      TransactionType.SalesDebitNote,
      TransactionType.SalesCreditNote,
      TransactionType.CashSales,
      TransactionType.CashSaleRefund,
      TransactionType.PurchaseRequisition,
      TransactionType.PurchaseOrder,
      TransactionType.GoodsReceivedNote,
      TransactionType.PurchaseInvoice,
      TransactionType.PurchaseDebitNote,
      TransactionType.PurchaseReturn,
      TransactionType.CashPurchases,
      TransactionType.CashPurchaseRefund,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer,
      TransactionType.StockIn,
      TransactionType.StockOut
    ],
    ESSENTIAL: [
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.StockIn,
      TransactionType.StockOut,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer
    ]
  },
  MY: {
    LITE: [
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.StockIn,
      TransactionType.StockOut,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer
    ],
    ESSENTIAL: [
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.StockIn,
      TransactionType.StockOut,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer
    ],
    PRIME: [
      TransactionType.Quotation,
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.SalesDebitNote,
      TransactionType.PurchaseRequisition,
      TransactionType.PurchaseOrder,
      TransactionType.GoodsReceivedNote,
      TransactionType.PurchaseDebitNote,
      TransactionType.CashPurchases,
      TransactionType.StockIn,
      TransactionType.StockOut,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer
    ],
    ACCOUNTANT: [
      TransactionType.Quotation,
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.SalesDebitNote,
      TransactionType.CashSales,
      TransactionType.PurchaseRequisition,
      TransactionType.PurchaseOrder,
      TransactionType.GoodsReceivedNote,
      TransactionType.PurchaseDebitNote,
      TransactionType.CashPurchases,
      TransactionType.StockIn,
      TransactionType.StockOut,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer
    ]
  },
  MM: {
    LITE: [
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.StockIn,
      TransactionType.StockOut,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer
    ],
    ESSENTIAL: [
      TransactionType.SalesOrder,
      TransactionType.DeliveryOrder,
      TransactionType.StockIn,
      TransactionType.StockOut,
      TransactionType.StockAdjustment,
      TransactionType.StockTransfer
    ]
  }
};
